<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid>
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>
      <div class="flex flex-col space-y-6">
        <!-- SAC Hero Card -->
        <v-card class="mb-6 sac-hero-card" elevation="4">
          <div class="sac-hero-header">
            <div class="sac-hero-header-content">
              <div class="d-flex align-center mb-2">
                <v-avatar size="48" class="mr-3 sac-hero-logo">
                  <v-icon size="28" color="white">mdi mdi-security</v-icon>
                </v-avatar>
                <div>
                  <h1 class="sac-hero-title">Security Assurance Cases</h1>
                  <p class="sac-hero-subtitle">
                    Build structured security arguments with evidence-based assurance
                  </p>
                </div>
              </div>
            </div>
          </div>

          <v-card-text class="pt-6">
            <p class="text-body-1 mb-5 sac-intro-text">
              A <strong>Security Assurance Case (SAC)</strong> provides a structured argument that
              your system meets its security requirements. Create, manage, and track your compliance
              cases below.
            </p>

            <!-- Workflow Section -->
            <div class="sac-workflow-section mb-6">
              <div class="sac-section-header mb-4">
                <v-icon color="primary" size="28" class="mr-2">mdi-rocket-launch</v-icon>
                <span class="text-h6 font-weight-bold">Security Assurance Case Workflow</span>
              </div>

              <v-row class="sac-workflow-steps">
                <v-col cols="12" md="3" class="sac-workflow-col">
                  <div class="sac-ws step-requirements">
                    <div class="sac-step-number">1</div>
                    <div class="sac-step-icon-wrapper requirements-icon">
                      <v-icon size="36" color="white">mdi-format-list-checks</v-icon>
                    </div>
                    <div class="sac-step-title">Requirements</div>
                    <div class="sac-step-description">
                      Define security standards your system must meet
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="3" class="sac-workflow-col">
                  <div class="sac-ws step-claims">
                    <div class="sac-step-number claims-num">2</div>
                    <div class="sac-step-icon-wrapper claims-icon">
                      <v-icon size="36" color="white">mdi-message-text-outline</v-icon>
                    </div>
                    <div class="sac-step-title">Claims</div>
                    <div class="sac-step-description">
                      Assert how your system satisfies requirements
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="3" class="sac-workflow-col">
                  <div class="sac-ws step-subclaims">
                    <div class="sac-step-number subclaims-num">3</div>
                    <div class="sac-step-icon-wrapper subclaims-icon">
                      <v-icon size="36" color="white">mdi-source-branch</v-icon>
                    </div>
                    <div class="sac-step-title">Sub-Claims</div>
                    <div class="sac-step-description">
                      Break claims into detailed supporting statements
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="3" class="sac-workflow-col">
                  <div class="sac-ws step-evidence">
                    <div class="sac-step-number evidence-num">4</div>
                    <div class="sac-step-icon-wrapper evidence-icon">
                      <v-icon size="36" color="white">mdi-fingerprint</v-icon>
                    </div>
                    <div class="sac-step-title">Evidence</div>
                    <div class="sac-step-description">
                      Link documentation and artifacts that prove your claims
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <v-divider class="my-5"></v-divider>

            <!-- Best Practices -->
            <v-row>
              <v-col cols="12" md="6">
                <div class="sac-feature-box">
                  <div class="sac-feature-box-header">
                    <v-icon color="primary" size="28" class="mr-2">mdi-lightbulb-on</v-icon>
                    <span class="text-h6 font-weight-bold">Best Practices</span>
                  </div>
                  <p class="text-body-2 grey--text text--darken-1 mt-2 mb-3">
                    Follow these guidelines for effective Security Assurance Case creation.
                  </p>
                  <ul class="sac-feature-list">
                    <li>
                      <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>
                      Clear argumentation from requirements to evidence
                    </li>
                    <li>
                      <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>
                      Use diverse types of evidence for stronger assurance
                    </li>
                    <li>
                      <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>
                      Complete coverage of all requirements
                    </li>
                    <li>
                      <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>
                      Maintain traceable links between components
                    </li>
                  </ul>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="sac-feature-box components-box">
                  <div class="sac-feature-box-header">
                    <v-icon color="success" size="28" class="mr-2">mdi-layers</v-icon>
                    <span class="text-h6 font-weight-bold">Security Assurance Case Components</span>
                  </div>
                  <p class="text-body-2 grey--text text--darken-1 mt-2 mb-3">
                    Core building blocks of your security case.
                  </p>
                  <ul class="sac-feature-list">
                    <li>
                      <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                      <strong>Requirements</strong> &mdash; Security standards to meet
                    </li>
                    <li>
                      <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                      <strong>Claims</strong> &mdash; High-level assertions
                    </li>
                    <li>
                      <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                      <strong>Sub-claims</strong> &mdash; Detailed supporting statements
                    </li>
                    <li>
                      <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                      <strong>Evidence</strong> &mdash; Proof artifacts and documentation
                    </li>
                  </ul>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <div class="sac-content-wrapper">
          <div class="sac-search-bar-container">
            <div class="sac-search-bar-header">
              <v-icon color="white" size="20" class="mr-2">mdi-magnify</v-icon>
              <span class="white--text font-weight-bold">Search &amp; Filter</span>
            </div>
            <div class="sac-search-bar-body d-flex align-center flex-wrap">
              <v-select
                v-model="searchField"
                :items="searchFields"
                item-text="text"
                item-value="value"
                :label="$t('search-field')"
                hide-details
                outlined
                dense
                class="search-field"
                style="max-width: 200px;"
              ></v-select>
              <v-text-field
                v-model="searchQuery"
                append-icon="mdi-magnify"
                :label="$t('search')"
                hide-details
                class="flex-grow-1"
                outlined
                dense
                @keyup.enter="applySearch"
              />
              <v-btn class="sac-create-btn" color="primary" @click="handleClickCreate()">
                <v-icon class="mr-2">mdi-plus</v-icon>
                {{ $t('sac.create') }}
              </v-btn>
            </div>
          </div>

          <v-card class="sac-table-card" elevation="2">
            <div class="sac-table-header">
              <v-icon color="white" class="mr-2">mdi-table</v-icon>
              <span class="white--text text-h6 font-weight-bold">Assurance Cases</span>
            </div>
            <v-data-table
              :headers="getHeaders"
              :items="securityAssuranceCases"
              :search="searchQuery"
              :custom-filter="customFilter"
              class="elevation-1 certifai-table"
              :loading="loading"
              :items-per-page="5"
              :footer-props="{
                showFirstLastPage: true,
                firstIcon: 'mdi-arrow-collapse-left',
                lastIcon: 'mdi-arrow-collapse-right',
                prevIcon: 'mdi-arrow-left',
                nextIcon: 'mdi-arrow-right',
                disableItemsPerPage: false,
                itemsPerPageText: getItemsPerPageText,
                showCurrentPage: true,
              }"
              must-sort
              :sort-desc="true"
            >
              <template v-slot:[`item.status`]="{ item }">
                <v-chip :color="statusChipColor(item.status)" outlined dark>
                  <span class="text-capitalize">{{ $t(item.status) }}</span>
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      class="g-action-btn g-action-view"
                      v-on="on"
                      v-bind="attrs"
                      @click="handlePreviewClick(item)"
                    >
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('preview') }}</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      class="g-action-btn g-action-delete"
                      v-on="on"
                      v-bind="attrs"
                      @click="enableDeleteDialog(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('preview') }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>
          <v-dialog v-model="deleteClicked" max-width="500">
            <v-card class="sac-dialog-card">
              <v-card-title class="sac-dialog-title">{{ $t('delete.confirm') }}</v-card-title>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="gray" text @click="deleteClicked = false">{{ $t('cancel') }}</v-btn>
                <v-btn color="red" text @click="deleteSac">{{ $t('delete.message') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';
import Loader from '@/components/helpers/Loader.vue';
import securityAssuranceCaseApi from '../api/securityAssuranceCaseApi';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';

export default {
  name: 'SecurityAssuranceCase',
  mixins: [util],
  components: {
    Loader,
    Breadcrumbs,
  },
  computed: {
    getItemsPerPageText() {
      const t = this.$t.bind(this);
      return t('sac.table-footer');
    },
    getHeaders() {
      const t = this.$t.bind(this);
      return [
        {
          text: t('sac.headers.id'),
          value: 'id',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: t('sac.headers.name'),
          value: 'name',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('sac.headers.description'),
          value: 'description',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '20%',
        },
        {
          text: t('sac.headers.date'),
          value: 'creationDate',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('sac.headers.version'),
          value: 'version',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('sac.actions'),
          value: 'actions',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
      ];
    },
    searchFields() {
      return [
        { text: this.$t('search-field-all'), value: 'all' },
        { text: this.$t('sac.headers.id'), value: 'id' },
        { text: this.$t('sac.headers.name'), value: 'name' },
        { text: this.$t('sac.headers.description'), value: 'description' },
        { text: this.$t('sac.headers.date'), value: 'creationDate' },
        { text: this.$t('sac.headers.version'), value: 'version' },
      ];
    },
  },
  data() {
    return {
      loading: false,
      searchQuery: '',
      searchField: 'all',
      deleteClicked: false,
      deleteItem: null,
      previewAssetClicked: false,
      securityAssuranceCases: [],
    };
  },
  created() {
    this.$emit('loading', true);
    this.loading = true;
    this.fetchSecurityAssuranceCases();
    this.$emit('loading', false);
    this.loading = false;
  },
  methods: {
    async fetchSecurityAssuranceCases() {
      try {
        const { data: rawSecurityAssuranceCases } = await securityAssuranceCaseApi.all();
        this.securityAssuranceCases = rawSecurityAssuranceCases;
      } catch (error) {
        if (error.response.status === 400) {
          EventBus.$emit('snackbar', {
            text: this.$t('errorList.error-404'),
            color: 'grey',
          });
        }
      }
    },
    enableDeleteDialog(item) {
      this.deleteItem = item;
      this.deleteClicked = true;
    },
    byteToUint8Array(byteArray) {
      var uint8Array = new Uint8Array(byteArray.length);
      for (var i = 0; i < uint8Array.length; i++) {
        uint8Array[i] = byteArray[i];
      }

      return uint8Array;
    },
    enableCreateDialog() {
      this.createClicked = true;
    },
    statusChipColor(status) {
      switch (status) {
        case 'Draft':
          return 'yellow';
        case 'Finalized':
          return 'green';
        default:
          return 'primary';
      }
    },
    async deleteSac() {
      this.loading = true;
      try {
        await securityAssuranceCaseApi.delete(this.deleteItem.id);
        // Find the index of the deleted item and remove it from the array
        const index = this.securityAssuranceCases.findIndex(
          securityAssuranceCase => securityAssuranceCase.id === this.deleteItem.id,
        );
        if (index !== -1) {
          this.securityAssuranceCases.splice(index, 1);
        }
        EventBus.$emit('snackbar', {
          text: this.$t('delete.success'),
          color: 'green',
        });
      } catch (error) {
        console.error('Error occurred during deletion:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.deleteClicked = false;
        this.loading = false;
      }
    },
    handleClickCreate() {
      localStorage.removeItem('socVersionId');
      localStorage.removeItem('sac');
      this.$router.push({
        name: 'SacBuilder',
      });
    },
    handlePreviewClick(item) {
      localStorage.setItem('sac', JSON.stringify(item));
      localStorage.setItem('socVersionId', JSON.stringify(item.socVersionId));
      this.$router.push({
        name: 'SacBuilder',
      });
    },
    customFilter(value, search, item) {
      if (!search) return true;

      search = search.toString().toLowerCase();

      if (this.searchField === 'all') {
        // Search in all fields (default behavior)
        return Object.keys(item).some(key => {
          // Skip action column and non-string values
          if (key === 'actions' || key === 'raw' || item[key] === null || item[key] === undefined) {
            return false;
          }

          try {
            const itemValue = item[key].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        });
      } else {
        // Search only in the selected field
        if (item[this.searchField] !== null && item[this.searchField] !== undefined) {
          try {
            const itemValue = item[this.searchField].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        }
        return false;
      }
    },
    applySearch() {
      // This method can be used for any additional search logic if needed
      // For now it's a placeholder as the filtering happens automatically
    },
  },
};
</script>

<style scoped>
/* ============================================
   SECURITY ASSURANCE CASE - Modern Design System
   ============================================ */

.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 40px;
}

.breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}

.search-field {
  min-width: 150px;
}

/* Hero Card */
.sac-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
  background: #ffffff;
}

.sac-hero-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  padding: 24px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.sac-hero-header-content {
  flex: 1;
}

.sac-hero-logo {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(4px);
}

.sac-hero-title {
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.sac-hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  margin: 0;
  font-weight: 400;
}

.sac-intro-text {
  color: #424242;
  line-height: 1.7;
  font-size: 1.05rem;
}

/* Workflow Section */
.sac-workflow-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  padding: 28px;
  margin-top: 8px;
}

.sac-section-header {
  display: flex;
  align-items: center;
}

.sac-workflow-steps {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: center;
}

.sac-ws {
  background: white;
  border-radius: 16px;
  padding: 24px 20px;
  text-align: center;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sac-ws:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
}

.sac-ws.step-requirements:hover {
  background: linear-gradient(135deg, #ffffff 0%, #e3f2fd 100%);
}
.sac-ws.step-claims:hover {
  background: linear-gradient(135deg, #ffffff 0%, #fff3e0 100%);
}
.sac-ws.step-subclaims:hover {
  background: linear-gradient(135deg, #ffffff 0%, #f3e5f5 100%);
}
.sac-ws.step-evidence:hover {
  background: linear-gradient(135deg, #ffffff 0%, #e8f5e9 100%);
}

.sac-step-number {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
}

.sac-step-number.claims-num {
  background: linear-gradient(135deg, #ff8f00, #ffb74d);
  box-shadow: 0 4px 12px rgba(255, 143, 0, 0.4);
}

.sac-step-number.subclaims-num {
  background: linear-gradient(135deg, #7b1fa2, #ba68c8);
  box-shadow: 0 4px 12px rgba(123, 31, 162, 0.4);
}

.sac-step-number.evidence-num {
  background: linear-gradient(135deg, #2e7d32, #66bb6a);
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.4);
}

.sac-step-icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 12px 0 16px 0;
  transition: all 0.3s ease;
}

.sac-step-icon-wrapper.requirements-icon {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  box-shadow: 0 8px 24px rgba(25, 118, 210, 0.3);
}

.sac-step-icon-wrapper.claims-icon {
  background: linear-gradient(135deg, #ff8f00, #ffb74d);
  box-shadow: 0 8px 24px rgba(255, 143, 0, 0.3);
}

.sac-step-icon-wrapper.subclaims-icon {
  background: linear-gradient(135deg, #7b1fa2, #ba68c8);
  box-shadow: 0 8px 24px rgba(123, 31, 162, 0.3);
}

.sac-step-icon-wrapper.evidence-icon {
  background: linear-gradient(135deg, #2e7d32, #66bb6a);
  box-shadow: 0 8px 24px rgba(46, 125, 50, 0.3);
}

.sac-ws:hover .sac-step-icon-wrapper {
  transform: scale(1.05);
}

.sac-step-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a237e;
  margin-bottom: 8px;
  line-height: 1.3;
}

.sac-step-description {
  color: #616161;
  font-size: 0.875rem;
  line-height: 1.6;
  flex-grow: 1;
}

/* Arrow connectors */
.sac-workflow-col {
  position: relative;
}

.sac-workflow-col:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 50%;
  right: -12px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 12px solid transparent;
  border-bottom: 12px solid transparent;
  border-left: 14px solid #1976d2;
  z-index: 10;
  filter: drop-shadow(2px 0 4px rgba(25, 118, 210, 0.3));
}

/* Feature Boxes */
.sac-feature-box {
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  height: 100%;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.sac-feature-box:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: #1976d2;
}

.sac-feature-box.components-box:hover {
  border-color: #4caf50;
}

.sac-feature-box-header {
  display: flex;
  align-items: center;
}

.sac-feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sac-feature-list li {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
  color: #424242;
  font-size: 0.9rem;
  line-height: 1.5;
}

.sac-feature-list li:last-child {
  border-bottom: none;
}

/* Content Wrapper */
.sac-content-wrapper {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* Search Bar */
.sac-search-bar-container {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.sac-search-bar-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
  padding: 10px 20px;
  display: flex;
  align-items: center;
}

.sac-search-bar-body {
  padding: 16px 20px;
  gap: 12px;
  background: #fafafa;
}

.sac-create-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 40px !important;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3) !important;
}

/* Table Card */
.sac-table-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
}

.sac-table-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
  padding: 14px 24px;
  display: flex;
  align-items: center;
}

/* Dialog */
.sac-dialog-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.sac-dialog-title {
  background: linear-gradient(135deg, #c62828 0%, #ef5350 100%);
  color: white !important;
}

/* Responsive */
@media (max-width: 960px) {
  .sac-hero-header {
    flex-direction: column;
    text-align: center;
  }
  .sac-hero-header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .sac-workflow-col:not(:last-child)::after {
    display: none;
  }
  .sac-ws {
    margin-bottom: 24px;
  }
}

@media (max-width: 600px) {
  .sac-hero-title {
    font-size: 1.4rem;
  }
  .sac-workflow-section {
    padding: 20px 16px;
  }
  .sac-step-icon-wrapper {
    width: 56px;
    height: 56px;
  }
}
</style>
